import stripWhitespace from 'jl-design-system/utils/string/stripWhitespace';
import { resetForm } from 'jl-design-system/redux/actions/form/formActions';
import { format } from 'date-fns';

import { SubmissionError } from 'redux-form';
import {
  APPLY_GIFT_CARD,
  REMOVE_GIFT_OPTION,
} from '@constants/actionConstants';
import { URL_GIFT_CARD_APPLY, URL_GIFT_CARD_REMOVE } from '@constants/endpointConstants';
import { APPLY_GIFT_CARD_FORM_ID } from '@utils/form/configs/giftOptions';
import errorCodeConstants from '@constants/errorCodeConstants';
import { GIFT_CARD_NUMBER, GIFT_CARD_PIN } from '@utils/form/configs/giftFields';
import handleInconsistentOrderFormState from '@utils/orderform/handleInconsistentOrderFormState';
import encrypt from '@utils/crypto/encrypt';
import determineSelectedPaymentType from './selectedPaymentTypeActions';

const {
  GIFT_CARD_PIN_INCORRECT,
  GIFT_CARD_NOT_RECOGNISED,
  DUPLICATE_GIFT_CARD,
  GIFT_CARD_EXPIRED,
  GIFT_CARD_NO_VALUE,
  GIFT_CARD_PAYMENT_LIMIT_REACHED,
} = errorCodeConstants;
const GIFT_PIN = [GIFT_CARD_PIN.id];
const GIFT_NUMBER = [GIFT_CARD_NUMBER.id];
const BOTH = [...GIFT_PIN, ...GIFT_NUMBER];

const errorLookup = {
  [GIFT_CARD_PIN_INCORRECT]: {
    causedBy: GIFT_PIN,
    message: "Please check your PIN and try again. If you enter it incorrectly more than 3 times, we'll lock the card for 8 hours",
  },
  [GIFT_CARD_NOT_RECOGNISED]: {
    causedBy: GIFT_NUMBER,
    message: 'Please check you have entered the gift card number correctly and try again',
  },
  [DUPLICATE_GIFT_CARD]: {
    causedBy: BOTH,
    message: 'You have already used this gift card',
  },
  [GIFT_CARD_EXPIRED]: {
    causedBy: BOTH,
    message: 'Unfortunately this gift card has expired',
  },
  [GIFT_CARD_NO_VALUE]: {
    causedBy: BOTH,
    message: 'There is no remaining balance on this gift card',
  },
  [GIFT_CARD_PAYMENT_LIMIT_REACHED]: {
    causedBy: BOTH,
    message: 'Sorry, you cannot use more than 100 gift cards in the same order',
  },
};

export const applyGiftCard = ({
  cardNumber = '',
  pin = '',
  id = '',
  isUndo = false,
} = {}) => async (dispatch, getState) => {
  const strippedCardNumber = stripWhitespace(cardNumber);
  const giftingPaymentDetails = getState().payment?.giftingPaymentDetails || {};

  const {
    clientReference,
    publicKey,
    salt,
    token,
  } = giftingPaymentDetails;

  let encryptedDetails;
  try {
    encryptedDetails = await dispatch(encrypt({
      value: `${strippedCardNumber}:${pin}:${clientReference}:${salt}:${format(new Date(), 'Hms')}`,
      publicKey,
    }));
  } catch (error) {

    return dispatch({
      type: `${APPLY_GIFT_CARD}.FAILED`,
      error: {
        code: errorCodeConstants.GIFT_CARD_SERVICE_UNAVAILABLE,
      },
      pin,
      cardNumber: strippedCardNumber,
      isUndo,
    });
  }

  const body = { encryptedDetails, token };

  const response = await dispatch({
    type: APPLY_GIFT_CARD,
    id,
    request: client => client({ path: URL_GIFT_CARD_APPLY, config: { body, method: 'POST' } }),
    cardNumber: strippedCardNumber,
    pin,
    isUndo,
  });

  if (response.type === `${APPLY_GIFT_CARD}.SUCCESS`) {
    dispatch(determineSelectedPaymentType());
    dispatch(resetForm(APPLY_GIFT_CARD_FORM_ID));
  }

  if (response.type === `${APPLY_GIFT_CARD}.FAILED`) {

    const code = response?.error?.code;
    const { causedBy, message } = errorLookup[code] || {};
    if (
      code === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE ||
      code === errorCodeConstants.INCOMPLETE_DELIVERY_DETAILS
    ) {
      await dispatch(handleInconsistentOrderFormState());
      return;
    }

    if (causedBy) {
      const errors = causedBy.reduce((acc, id) => ({
        ...acc,
        [id]: message,
      }), {});

      throw new SubmissionError(errors);
    }
  }
};

export const callRemoveGiftOption = (item, removalContext) => ({
  type: REMOVE_GIFT_OPTION,
  request: client => client({ path: URL_GIFT_CARD_REMOVE(item.id), config: { method: 'DELETE' } }),
  item,
  removalContext,
});

export const removeGiftOption = (item = {}) => async (dispatch, getState) => {
  const state = getState();
  const modalOpen = state.payment.giftOptionsModalOpen;
  const context = modalOpen ? 'modal' : 'payment';

  const response = await dispatch(callRemoveGiftOption(item, context));

  if (response.type === `${REMOVE_GIFT_OPTION}.FAILED`) {
    const code = response?.error?.code;
    if (
      code === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE ||
      code === errorCodeConstants.INCOMPLETE_DELIVERY_DETAILS
    ) {
      await dispatch(handleInconsistentOrderFormState());
      return undefined;
    }
  }
  return response;
};
